import { useState, useEffect, useCallback } from "react"
import { useLocation } from "react-router"

type BlessedState = "unknown" | "yes" | "no"

type PostMessageType = "ready" | "show" | "close"

export interface ChurchCenterIframeState {
  close: () => void
  inIframe: boolean
  blessed: BlessedState
}

function postMessageToChurchCenterButton(type: PostMessageType): void {
  window.parent?.postMessage({ type }, "*")
}

function handleKeyup(event: KeyboardEvent): void {
  if (event.keyCode === 27) {
    if (event.target === document.body) {
      postMessageToChurchCenterButton("close")
    } else if ((event.target as HTMLElement)?.blur) {
      ;(event.target as HTMLElement).blur()
    }
  }
}

function isModalIframe(search: string): boolean {
  try {
    // Don't treat as iframe if we have the embed URL parameter.
    // The embed param is used for embedded calendar views and forms that should
    // be iframeable but not styled as modal dialogs. See ReactSinglePageAppController
    // which allows embedding when embed=true for calendar or people/forms paths.
    const urlParams = new URLSearchParams(search)
    if (urlParams.has("embed")) {
      return false
    }
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

export function useChurchCenterIframe(): ChurchCenterIframeState {
  const [blessed, setBlessed] = useState<BlessedState>("unknown")
  const { search } = useLocation()
  const [inIframe] = useState(isModalIframe(search))

  const confirmBlessed = useCallback(() => {
    setBlessed("yes")
  }, [])

  const assumeNotBlessed = useCallback(() => {
    if (blessed === "unknown") {
      setBlessed("no")
      console.log(
        "Trying to put Giving on your website? Use the fully supported ChurchCenterModal! Instructions are available at https://help.planningcenter.com/en/144372-link-to-your-donation-form-on-your-website.html",
      )
    }
  }, [blessed])

  const close = useCallback(() => {
    postMessageToChurchCenterButton("close")
  }, [])

  const receiveMessageFromChurchCenterButton = useCallback(
    (e: MessageEvent) => {
      const location = new URL(e.origin)
      const isHTTPS =
        location.protocol === "https:" ||
        location.host.slice(0, 9) === "localhost" ||
        location.host.match(/\.test$/)

      if (e.data.type === "verified") {
        if (isHTTPS) {
          confirmBlessed()
          postMessageToChurchCenterButton("show")
        } else {
          throw "ChurchCenterButton: must be called from an HTTPS page."
        }
      }
    },
    [confirmBlessed],
  )

  const showIFrame = useCallback(() => {
    window.scrollTo(0, 0)
    postMessageToChurchCenterButton("ready")
    setTimeout(assumeNotBlessed, 1000)
  }, [assumeNotBlessed])

  useEffect(() => {
    if (!inIframe) return

    window.addEventListener(
      "message",
      receiveMessageFromChurchCenterButton,
      false,
    )
    document.addEventListener("keyup", handleKeyup)

    showIFrame()

    return () => {
      window.removeEventListener(
        "message",
        receiveMessageFromChurchCenterButton,
      )
      document.removeEventListener("keyup", handleKeyup)
    }
  }, [inIframe, receiveMessageFromChurchCenterButton, showIFrame])

  return {
    inIframe,
    blessed,
    close,
  }
}
